<template>
  <div v-if="!createStore.ordered && createStore.hasInvitees">
    <div
      v-for="signee in createStore.signeesOrderedFlat"
      :key="signee.signeeId"
      class="my-3"
    >
      <div class="d-flex flex-row align-center">
        <EmailSuggestion
          :id="`email-${signee.signeeId}`"
          v-model.trim="signee.email"
          :autocomplete="`signee-email-${signee.email}-${signee.signeeId}`"
          :data-test-id="`signee-email-${signee.email}`"
          :enable-groups="signee.signeeType === SigneeType.SIGN"
          :label="getFieldLabel(signee.signeeType)"
          :name="`signee-email-${signee.signeeId}`"
          :rules="[ruleEmail]"
          :validate-on="getValidationEvent(signee.email)"
          @blur="emit('on-blur', signee)"
          @focus="emit('on-focus', signee)"
          @group-members-selected="
            (emails) => onGroupMembersSelected(emails, signee)
          "
          @keydown.enter="emit('on-update-key-down', { event: $event, signee })"
          @update:model-value="emit('on-update', { event: $event, signee })"
        >
          <template #prepend-inner>
            <v-icon v-if="createStore.hasApprovers" color="primary">{{
              signee?.signeeType === SigneeType.SIGN
                ? 'far fa-pen-nib'
                : 'fal fa-octagon-check'
            }}</v-icon>
          </template>
        </EmailSuggestion>

        <v-btn
          :aria-label="t('buttons.remove_signee')"
          class="mt-1 ml-1 text-grey"
          color="default"
          data-test-id="remove-signee"
          icon="fal fa-xmark"
          size="medium"
          @click="emit('remove-invitee', signee)"
        >
          <v-icon>fal fa-xmark</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Quorum (requiredSignatures) without a sign order: the invitees share
         one signee group, so the quorum applies to the whole list rather than
         to a sequence step. Approvers make that shared bucket mixed, which
         never supports a quorum — the control then shows why instead (see
         useQuorumEligibility). -->
    <GroupQuorumControl
      v-if="quorumControl && canShowUngroupedQuorum"
      :mixed-group="isUngroupedMixed"
      :quorum="createStore.getGroupQuorum(ungroupedParticipants)"
      :total="ungroupedParticipants.length"
      ungrouped
      @change="
        (value) => createStore.setGroupQuorum(ungroupedParticipants, value)
      "
    />
  </div>
</template>

<script setup lang="ts">
import EmailSuggestion from '@/components/inputs/Emailsuggestion/EmailSuggestion.vue';
import GroupQuorumControl from '@/components/create/GroupQuorumControl.vue';
import { useCreateStore } from '@/stores/create.ts';
import { useQuorumEligibility } from '@/components/create/composables/useQuorumEligibility.ts';
import { useQuorumControlSetting } from '@/composables/useQuorumControlSetting.ts';
import type { Signee } from '@/types/Signee.ts';
/*import { computed } from 'vue';
import { useDocumentAuth } from '@/composables/useDocumentAuth.ts';*/
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { useI18n } from 'vue-i18n';
import { ruleEmail } from '@deepcloud/field-validators';
import { persistSigneePlaceholders } from '@/composables/placement/usePlacementUtility';
import { buildFileAutographPositionsFromSignings } from '@/utils/signeeSignings.ts';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import { useGlobalStore } from '@/stores/global';
import { useDisplay } from 'vuetify';
import { useEmitter } from '@/composables/useEmitter';

const createStore = useCreateStore();
const globalStore = useGlobalStore();
//const documentAuth = useDocumentAuth();
const display = useDisplay();
const emitter = useEmitter();
const { t } = useI18n();
const { canShowUngroupedQuorum, isUngroupedMixed, ungroupedParticipants } =
  useQuorumEligibility();
const { quorumControl } = useQuorumControlSetting();

const { placePlaceholdersAutomatically, placeholderTemplateRefs } =
  injectStrict(PlaceHolderComposableKey);

const emit = defineEmits([
  'remove-invitee',
  'on-update-invitee',
  'on-blur',
  'on-focus',
  'on-update',
  'on-update-key-down'
]);

function getValidationEvent(email: string | null) {
  if (!email) {
    return 'submit';
  } else {
    return 'blur';
  }
}

/*
function isRequestorSignee(signee: Signee) {
  return (
    signee.signeeId === createStore.initiatorAsSignee?.signeeId &&
    !isDocumentAuthAuthenticated.value
  );
}
*/
/*

const isDocumentAuthAuthenticated = computed(() => {
  return documentAuth.isAuthenticated.value;
});
*/

function getFieldLabel(signeeType: SigneeType) {
  return signeeType === SigneeType.SIGN
    ? t('labels.signee_email')
    : t('labels.approver_email');
}

// Unordered mode: the first email updates the triggering field via API remaining emails get created
// as new invitees and pushed into signeesOrdered[0].
async function onGroupMembersSelected(
  emails: string[],
  signee: Signee
): Promise<void> {
  if (emails.length === 0) {
    emitter.$emit('close-group-members-dialog');
    return;
  }

  try {
    // Capture before adds: new signees clear per-file completeness and the
    // PlaceSignaturesSwitch watcher sets placeSignaturesModel false.
    //
    // Skipped while the signing-workflow dialog defers persistence: the invitees
    // added here only exist in the local draft, so auto-placing them would draw
    // their placeholders on the PDF behind the open dialog before Confirm.
    // useSigningWorkflow.confirm() places them once they really exist.
    const shouldAutoPlace =
      createStore.placeSignaturesModel && !createStore.deferPersistence;
    // Update the exact field that triggered the dialog with the first email
    const response = await createStore.updateInvitee(
      createStore.document!.documentId,
      signee.signeeId,
      {
        email: emails[0] === '' ? null : emails[0],
        ...(signee.policy?.canModifyAutographPosition && {
          autographPosition: signee.autographPosition,
          fileAutographPositions:
            buildFileAutographPositionsFromSignings(signee.signings) ?? {}
        }),
        comment: null,
        signOrder: signee.signOrder
      }
    );

    createStore.updateInviteeInLocalData(response.data);

    // Create new invitees for the remaining emails
    const remainingEmails = emails.slice(1);

    if (remainingEmails.length > 0) {
      const results = await Promise.all(
        remainingEmails.map((email) =>
          createStore.addInvitee(createStore.document!.documentId, {
            email,
            signeeType: SigneeType.SIGN
          })
        )
      );
      results.forEach((res) => {
        createStore.signeesOrdered[0].push(res.data);
      });
    }

    if (shouldAutoPlace) {
      await placeSignaturesAutomatically();
    }
  } catch (e) {
    console.error('Failed to add group members:', e);
  } finally {
    emitter.$emit('close-group-members-dialog');
  }
}

async function placeSignaturesAutomatically(): Promise<void> {
  setTimeout(async () => {
    if (createStore.filteredSignees) {
      const placeholdersToUpdate = await placePlaceholdersAutomatically(null);

      await persistSigneePlaceholders(
        createStore,
        placeholdersToUpdate,
        placeholderTemplateRefs?.value,
        createStore.filteredSignees,
        createStore.document
      );

      if (display.mdAndDown.value) {
        globalStore.drawer = false;
      }
    }
  }, 600);
}
</script>
